/*
 * GetStream Chat Implementation
 *
 * This is the current production chat system using GetStream.
 *
 * For the legacy PubNub implementation, see:
 * ./legacy_pubnub/README.md
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Channel,
  ChannelHeader,
  ChannelList,
  MessageList,
  MessageInput,
  Window,
  useChatContext,
  usePaginatedChannels
} from 'stream-chat-react';
import { format, isToday, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

export default function ChatWrapper({ setToken, user_id, is_supervisor }) {
  const { client } = useChatContext();
  const [currentChannel, setCurrentChannel] = useState(null);
  const [isMarkingUnread, setIsMarkingUnread] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [forceUpdate, setForceUpdate] = useState(0); // For forcing re-renders on real-time updates
  const [hasRealtimeUpdate, setHasRealtimeUpdate] = useState(false); // Visual indicator for real-time updates
  const [channelListKey, setChannelListKey] = useState(0); // Key to force channel list re-mount

  // Custom channel filters - show all channels for the user with search
  const channelFilters = useMemo(() => ({
    type: 'messaging',
    members: { $in: [user_id] },
    ...(searchQuery && {
      $or: [
        { name: { $autocomplete: searchQuery } },
        { 'member.user.name': { $autocomplete: searchQuery } }
      ]
    })
  }), [user_id, searchQuery]);

  const channelSort = useMemo(() => [
    { unread_count: -1 },
    { last_message_at: -1 },
    { priority: 1 },
    { name: 1 },
  ], []);

  const channelOptions = useMemo(() => ({
    limit: 20,
    presence: true,
    state: true,
    watch: true,
    // Include forceUpdate to trigger re-renders on real-time updates
    _forceUpdate: forceUpdate
  }), [forceUpdate]);

  // Use Stream's built-in pagination hook
  const {
    channels,
    hasNextPage,
    loadNextPage,
    loading,
    refreshing,
  } = usePaginatedChannels(
    client,
    channelFilters,
    channelSort,
    channelOptions
  );

  // Function to mark channel as unread
  const markAsUnread = useCallback(async () => {
    if (!currentChannel || isMarkingUnread) return;

    setIsMarkingUnread(true);

    try {
      // Get the messages already loaded in the channel
      const messages = currentChannel.state.messages;

      if (!messages || messages.length === 0) {
        alert('Nessun messaggio da segnare come non letto.');
        setIsMarkingUnread(false);
        return;
      }

      // Find the last message that is NOT from the current user
      // Or use the second-to-last message if all messages are from current user
      let messageToMarkFrom = null;

      // First, try to find the last message from another user
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].user?.id !== user_id) {
          messageToMarkFrom = messages[i];
          break;
        }
      }

      // If all messages are from current user, use the second-to-last message
      if (!messageToMarkFrom && messages.length >= 2) {
        messageToMarkFrom = messages[messages.length - 2];
      }

      // Otherwise use the last message
      if (!messageToMarkFrom) {
        messageToMarkFrom = messages[messages.length - 1];
      }

      if (!messageToMarkFrom || !messageToMarkFrom.id) {
        alert('Impossibile trovare un messaggio valido.');
        setIsMarkingUnread(false);
        return;
      }

      // Use Stream Chat's markUnread API
      // This sets the read state to BEFORE this message, making this and all after it unread
      await currentChannel.markUnread({
        message_id: messageToMarkFrom.id
      });

      // Close the current channel to prevent it from being marked as read again
      setCurrentChannel(null);

      // Force re-render of channel list to show unread badge
      setChannelListKey((prev) => prev + 1);

      // Show success feedback
      setShowSuccess(true);

      // Hide success message after delay
      setTimeout(() => {
        setShowSuccess(false);
      }, 2000);

    } catch (error) {
      console.error('Error marking channel as unread:', error);
      alert('Errore nel segnare come non letto. Riprova.');
    } finally {
      setIsMarkingUnread(false);
    }
  }, [currentChannel, isMarkingUnread, user_id]);

  useEffect(() => {
    client.on('connection.recovered', () => {
      console.log('Connection recovered');
    });

    client.on('connection.changed', (event) => {
      console.log('Connection changed:', event);
    });

    // Listen for new messages to update channel list in real-time
    client.on('message.new', (event) => {
      console.log('New message received:', event);
      // Force re-render to update channel preview with new message
      setForceUpdate((prev) => prev + 1);
      setHasRealtimeUpdate(true);
      setTimeout(() => setHasRealtimeUpdate(false), 2000);
    });

    // Listen for channel updates (like unread count changes)
    client.on('channel.updated', (event) => {
      console.log('Channel updated:', event);
      setForceUpdate(prev => prev + 1);
    });

    // Listen for notification.message_new for unread count updates
    client.on('notification.message_new', (event) => {
      console.log('New message notification:', event);
      setForceUpdate(prev => prev + 1);
    });

    // Listen for notification.mark_read to update unread counts
    client.on('notification.mark_read', (event) => {
      console.log('Messages marked as read:', event);
      setForceUpdate(prev => prev + 1);
    });

    // Listen for notification.mark_unread
    client.on('notification.mark_unread', (event) => {
      console.log('Messages marked as unread:', event);
      setForceUpdate(prev => prev + 1);
    });

    return () => {
      client.off('connection.recovered');
      client.off('connection.changed');
      client.off('message.new');
      client.off('channel.updated');
      client.off('notification.message_new');
      client.off('notification.mark_read');
      client.off('notification.mark_unread');
    };
  }, [client]);

  // Reset pagination when search changes
  // The usePaginatedChannels hook will automatically refresh when filters change
  // since we're using useMemo for channelFilters

  const customChannelPreview = (props) => {
    const { channel, latestMessagePreview, lastMessage, unread } = props;

    const formatMessageDate = (date) => {
      if (!date) return '';

      const messageDate = new Date(date);

      // Show time for today's messages, otherwise show relative time
      if (isToday(messageDate)) {
        return format(messageDate, 'HH:mm', { locale: it });
      }

      return formatDistanceToNow(messageDate, {
        addSuffix: true,
        locale: it
      });
    };

    // Get the last message timestamp from channel state or last message
    const lastMessageAt = channel.state.last_message_at || lastMessage?.created_at;

    // Check if this is a priority customer channel
    const isPriorityChannel = channel.data?.priority === true;

    // Truncate message preview to 50 characters
    const messagePreview = latestMessagePreview || lastMessage?.text || 'Nessun messaggio';
    const truncatedMessage = messagePreview.length > 50
      ? messagePreview.substring(0, 50) + '...'
      : messagePreview;

    return (
      <div
        className={`channel-preview p-3 border-b cursor-pointer hover:bg-gray-50 flex items-center justify-between ${
          currentChannel?.id === channel.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
        } ${isPriorityChannel ? 'border-l-2 border-yellow-500' : ''}`}
        onClick={() => setCurrentChannel(channel)}
      >
        <div className="channel-preview__content flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center flex-1 min-w-0">
              <h4 className="channel-preview__title font-semibold text-sm flex-1 truncate">
                {isPriorityChannel && (<span>⭐</span>)} {channel.data.name || `Customer ${channel.id.replace('customer-', '')}`}
              </h4>
            </div>
            {lastMessageAt && (
              <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                {formatMessageDate(lastMessageAt)}
              </span>
            )}
          </div>
          <p
            className="channel-preview__message text-xs text-gray-600"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
            title={messagePreview}
          >
            {truncatedMessage}
          </p>
        </div>
        {unread > 0 && (
          <div className="channel-preview__unread bg-red-500 text-white rounded-full px-2 py-1 text-xs min-w-[20px] h-5 flex items-center justify-center ml-2 flex-shrink-0">
            {unread}
          </div>
        )}
      </div>
    );
  };

  // Scroll handler for infinite scroll using usePaginatedChannels
  const handleChannelListScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const threshold = 100; // Load more when 100px from bottom

    if (scrollHeight - scrollTop <= clientHeight + threshold && hasNextPage && !loading) {
      loadNextPage();
    }
  };

  return (
    <div className="chat-app flex h-full bg-white overflow-hidden">
      {/* Channel List Sidebar */}
      <div className="w-80 bg-white border-r flex flex-col h-full">
        {/* Search */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-900">Messaggi</h2>
            {hasRealtimeUpdate && (
              <div className="flex items-center text-green-600 text-xs">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-1"></div>
                Aggiornato
              </div>
            )}
          </div>
          <input
            type="text"
            placeholder="Cerca conversazioni..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Channel List with scroll handler */}
        <div
          className="flex-1 overflow-y-auto"
          onScroll={handleChannelListScroll}
          key={channelListKey}
        >
          {/* Custom channel list using channels from hook */}
          {channels.map((channel, index) => (
            <div key={`${channel.id}-${forceUpdate}-${channelListKey}-${index}`}>
              {customChannelPreview({
                channel,
                latestMessagePreview: channel.state.messages[channel.state.messages.length - 1]?.text,
                lastMessage: channel.state.messages[channel.state.messages.length - 1],
                unread: channel.countUnread()
              })}
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className="p-4 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="ml-2">Caricamento...</span>
            </div>
          )}

          {/* End of list indicator */}
          {!hasNextPage && !loading && channels.length > 0 && (
            <div className="p-4 text-center text-gray-400 text-sm">
              Non ci sono altre conversazioni
            </div>
          )}

          {/* Empty state */}
          {!loading && channels.length === 0 && (
            <div className="p-4 text-center text-gray-400 text-sm">
              {searchQuery ? 'Nessuna conversazione trovata' : 'Nessuna conversazione'}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="channel-container flex-1 flex flex-col">
        {currentChannel ? (
          <Channel channel={currentChannel}>
            <Window>
              <div className={`flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0 ${
                currentChannel.data?.priority ? 'bg-yellow-50 border-yellow-200' : 'bg-white'
              }`}>
                <div className="flex items-center">
                  <ChannelHeader />
                  {currentChannel.data?.priority && (
                    <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      ⭐ Cliente Priorità
                    </span>
                  )}
                </div>
                <button
                  onClick={markAsUnread}
                  disabled={isMarkingUnread || !currentChannel?.state?.messages?.length}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors duration-200 flex items-center gap-2 font-medium ${
                    isMarkingUnread || !currentChannel?.state?.messages?.length
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-50 hover:bg-blue-100 text-blue-700'
                  }`}
                  title="Segna come non letto"
                >
                  {isMarkingUnread ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Caricamento...
                    </>
                  ) : showSuccess ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Fatto!
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"
                        />
                        <line x1="1" y1="1" x2="23" y2="23" strokeWidth={2} />
                      </svg>
                      Non letto
                    </>
                  )}
                </button>
              </div>
              <MessageList
                messageActions={['edit', 'delete', 'react']}
              />
              <MessageInput />
            </Window>
          </Channel>
        ) : (
          <div className="empty-state flex items-center justify-center h-full bg-gray-50">
            <div className="text-center">
              <div className="text-4xl text-gray-400 mb-4">💬</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Benvenuto in GS Loft Chat
              </h3>
              <p className="text-gray-500">
                Seleziona una conversazione per iniziare a messaggiare
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
